import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, MapPin, Clock, CheckCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const CONTACT_FUNCTION_ENDPOINT = '/.netlify/functions/send-contact-email';

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  company: z.string().optional(),
  budget: z.string().optional(),
  services: z.array(z.string()).optional(),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

const serviceOptions = [
  'Social Media Management',
  'Affiliate Marketing',
  'Email Marketing',
  'Ads Management',
  'Content Strategy',
  'Analytics & Reporting',
];

const budgetOptions = [
  'Under $1,000',
  '$1,000 - $3,000',
  '$3,000 - $5,000',
  '$5,000 - $10,000',
  '$10,000+',
];

export const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      services: [],
    },
  });

  const handleServiceToggle = (service) => {
    const updated = selectedServices.includes(service)
      ? selectedServices.filter((s) => s !== service)
      : [...selectedServices, service];
    setSelectedServices(updated);
    setValue('services', updated);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        services: selectedServices,
      };

      const response = await fetch(CONTACT_FUNCTION_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      reset();
      setSelectedServices([]);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding bg-forest" data-testid="contact-hero">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-6 block">
              Get In Touch
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.1] mb-6">
              Let's Start Your{' '}
              <span className="text-gold">Project</span>
            </h1>
            <p className="text-xl text-cream/80 max-w-2xl leading-relaxed">
              Ready to unlock your digital potential? Fill out the form below and we'll 
              get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-cream" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-bold text-forest mb-8">Contact Information</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-forest-dark" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest mb-1">Email</h3>
                    <p className="text-forest/70">hello@warnerdigital.org</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-forest-dark" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest mb-1">Location</h3>
                    <p className="text-forest/70">Remote / United States</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-forest-dark" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest mb-1">Response Time</h3>
                    <p className="text-forest/70">Within 24 hours</p>
                  </div>
                </div>
              </div>

              <div className="bg-forest-dark rounded-2xl p-6">
                <h3 className="font-semibold text-cream mb-4">What to Expect</h3>
                <ul className="space-y-3">
                  {[
                    'Initial consultation call',
                    'Custom strategy proposal',
                    'Transparent pricing',
                    'Ongoing support & reporting',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-cream/70">
                      <CheckCircle className="text-gold flex-shrink-0" size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {isSubmitted ? (
                <div className="bg-cream-paper border border-forest/10 rounded-2xl p-12 text-center" data-testid="success-message">
                  <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-forest-dark" size={40} />
                  </div>
                  <h2 className="text-3xl font-bold text-forest mb-4">Message Sent!</h2>
                  <p className="text-forest/70 text-lg mb-8">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    className="bg-forest text-cream hover:bg-forest-dark rounded-full px-8 py-6"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)} 
                  className="bg-cream-paper border border-forest/10 rounded-2xl p-8 lg:p-12"
                  data-testid="contact-form"
                >
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name" className="text-forest font-medium mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        {...register('name')}
                        placeholder="John Doe"
                        className="bg-white/50 border-forest/20 focus:border-gold rounded-xl h-12"
                        data-testid="input-name"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="email" className="text-forest font-medium mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder="john@example.com"
                        className="bg-white/50 border-forest/20 focus:border-gold rounded-xl h-12"
                        data-testid="input-email"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Company */}
                    <div>
                      <Label htmlFor="company" className="text-forest font-medium mb-2 block">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        {...register('company')}
                        placeholder="Your Company"
                        className="bg-white/50 border-forest/20 focus:border-gold rounded-xl h-12"
                        data-testid="input-company"
                      />
                    </div>

                    {/* Budget */}
                    <div>
                      <Label htmlFor="budget" className="text-forest font-medium mb-2 block">
                        Monthly Budget
                      </Label>
                      <select
                        id="budget"
                        {...register('budget')}
                        className="w-full h-12 px-4 bg-white/50 border border-forest/20 focus:border-gold rounded-xl text-forest"
                        data-testid="select-budget"
                      >
                        <option value="">Select budget range</option>
                        {budgetOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Services */}
                  <div className="mb-6">
                    <Label className="text-forest font-medium mb-4 block">
                      Services Interested In
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {serviceOptions.map((service) => (
                        <label
                          key={service}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                            selectedServices.includes(service)
                              ? 'bg-gold/20 border-gold'
                              : 'bg-white/50 border-forest/20 hover:border-gold/50'
                          }`}
                          data-testid={`service-checkbox-${service.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Checkbox
                            checked={selectedServices.includes(service)}
                            onCheckedChange={() => handleServiceToggle(service)}
                            className="border-forest/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                          />
                          <span className="text-sm text-forest">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-8">
                    <Label htmlFor="message" className="text-forest font-medium mb-2 block">
                      Project Details *
                    </Label>
                    <Textarea
                      id="message"
                      {...register('message')}
                      placeholder="Tell us about your project, goals, and timeline..."
                      rows={5}
                      className="bg-white/50 border-forest/20 focus:border-gold rounded-xl resize-none"
                      data-testid="input-message"
                    />
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold text-forest-dark hover:bg-gold-light rounded-full py-6 text-lg font-semibold disabled:opacity-50"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
